/**
 * DixonColesMethodology - Collapsible methodology explanation
 */

import React, { useState } from 'react';

export function DixonColesMethodology() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="dc-methodology">
      <button
        className="methodology-toggle"
        onClick={() => setIsExpanded(!isExpanded)}
        type="button"
        aria-expanded={isExpanded}
      >
        <span className="methodology-summary">
          Dixon-Coles is a statistical model for football match prediction that
          improves on basic Poisson by accounting for home advantage and low-score
          correlation.
        </span>
        <svg
          className={`expand-icon ${isExpanded ? 'expanded' : ''}`}
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="2" fill="none" />
        </svg>
      </button>

      {isExpanded && (
        <div className="methodology-content">
          <h3>How Dixon-Coles Works</h3>

          <section>
            <h4>Team Parameters</h4>
            <p>
              Each team has separate attack and defense strength ratings, estimated
              using Maximum Likelihood Estimation (MLE) on historical match data.
              These parameters capture how good a team is at scoring and preventing
              goals relative to the league average.
            </p>
          </section>

          <section>
            <h4>Home Advantage</h4>
            <p>
              The model includes a home advantage multiplier (typically around 1.1)
              that boosts the home team's expected goals. This accounts for the
              well-documented effect of playing at home.
            </p>
          </section>

          <section>
            <h4>Low-Score Correlation (Tau Correction)</h4>
            <p>
              Unlike basic Poisson models, Dixon-Coles corrects for the observed
              tendency of low-scoring games (0-0, 1-0, 0-1, 1-1) to occur more or
              less frequently than independent Poisson would predict. This is
              controlled by the rho parameter, typically around -0.13.
            </p>
          </section>

          <section>
            <h4>Probability Calculation</h4>
            <ol>
              <li>Calculate expected goals for each team using attack/defense parameters</li>
              <li>Generate scoreline probabilities using Poisson distribution</li>
              <li>Apply tau correction to adjust low-score probabilities</li>
              <li>Sum scoreline probabilities to get 1X2, Over/Under, and BTTS markets</li>
            </ol>
          </section>

          <section>
            <h4>Understanding Edge</h4>
            <p>
              <strong>Edge</strong> = Dixon-Coles Probability - Implied Odds Probability
            </p>
            <p>
              A positive edge suggests the bookmaker's odds undervalue this outcome.
              For example, if D&C gives Home Win 45% probability but odds imply 40%,
              there's a +5% edge.
            </p>
            <p className="methodology-note">
              Note: Positive edge doesn't guarantee profit - it indicates potential
              value based on the model. Always bet responsibly.
            </p>
          </section>
        </div>
      )}
    </div>
  );
}
